import { Link } from "react-router-dom";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowUpRight, Flame, Zap } from "lucide-react";
import { useI18n } from "@/i18n";
import { MaskLine, EASE } from "@/components/motion";
import { img } from "@/lib/gallery";

const Fact = ({ label, value, testid }) => (
  <div className="border-t border-white/15 pt-4 md:border-t-0 md:border-l md:pl-6 md:pt-0" data-testid={testid}>
    <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--steel-dim)]">{label}</p>
    <p className="font-display mt-1 text-2xl font-bold uppercase leading-tight text-white">{value}</p>
  </div>
);

const ProductFrame = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 110, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 110, damping: 18 });
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 900], [0, 90]);
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  return (
    <motion.div style={{ y }} className="relative [perspective:1400px]" onMouseMove={onMove} onMouseLeave={() => { mx.set(0); my.set(0); }}>
      <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.4, ease: EASE, delay: 0.3 }} style={{ rotateX: rx, rotateY: ry }} className="relative">
        <div className="chamfer relative aspect-[4/3] overflow-hidden bg-[var(--navy)]">
          <img src={img(1)} alt="MB300 — micro-boulangerie modulaire, vue avant 3/4" className="h-full w-full object-cover" data-testid="hero-product-image" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_30%,transparent_40%,rgba(7,12,26,0.55))]" />
        </div>
        <div className="pointer-events-none absolute -bottom-7 left-6 right-10 flex items-center gap-3 font-mono text-[11px] text-[var(--gold)]" style={{ transform: "translateZ(40px)" }}>
          <span className="h-px flex-1 bg-[var(--gold)]/60" />≈ 3 850 mm<span className="h-px flex-1 bg-[var(--gold)]/60" />
        </div>
        <div className="pointer-events-none absolute -right-7 top-8 bottom-8 hidden sm:flex flex-col items-center gap-3 font-mono text-[11px] text-[var(--gold)]">
          <span className="w-px flex-1 bg-[var(--gold)]/60" /><span className="[writing-mode:vertical-rl]">≈ 2 200 mm</span><span className="w-px flex-1 bg-[var(--gold)]/60" />
        </div>
      </motion.div>
    </motion.div>
  );
};

export const Hero = () => {
  const { t, eur, lang } = useI18n();
  const h = t.hero;
  return (
    <section id="mb300" className="grain relative overflow-hidden bg-[var(--night)] pt-28 pb-14 lg:pt-32" data-testid="hero-section">
      <div className="container-x relative" key={lang}>
        <div className="grid items-center gap-14 lg:grid-cols-12 lg:gap-10">
          <div className="lg:col-span-6">
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="font-mono text-xs uppercase tracking-[0.3em] text-[var(--gold)]">
              {h.eyebrow}
            </motion.p>
            <h1 className="mt-4" data-testid="hero-title">
              <MaskLine className="font-display text-[34vw] font-black leading-[0.78] tracking-tight sm:text-[11rem] lg:text-[12.5rem] xl:text-[14rem]">
                MB<span className="text-[var(--gold)]">300</span>
              </MaskLine>
              <MaskLine delay={0.15} className="font-display mt-3 text-2xl font-bold uppercase tracking-[0.04em] text-[var(--steel)] sm:text-3xl">
                {h.title}
              </MaskLine>
            </h1>
            <div className="mt-8 font-display text-4xl font-extrabold uppercase leading-[0.95] sm:text-5xl">
              <MaskLine delay={0.3}>{h.tagline[0]}</MaskLine>
              <MaskLine delay={0.42} className="text-outline">{h.tagline[1]}</MaskLine>
            </div>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.8, ease: EASE }} className="mt-8 flex items-baseline gap-3" data-testid="hero-capacity">
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--steel-dim)]">{h.capLabel}</span>
              <span className="font-display text-4xl font-black uppercase text-white sm:text-5xl">{h.capValue}</span>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.82, duration: 0.7, ease: EASE }}
              className="mt-6 flex max-w-xl items-center gap-3 border-l-2 border-[var(--gold)] pl-4 text-sm font-semibold text-white" data-testid="hero-energy">
              <Flame className="h-5 w-5 shrink-0 text-[var(--gold)]" aria-hidden="true" />
              <Zap className="h-5 w-5 shrink-0 text-[var(--gold-hi)]" aria-hidden="true" />
              <span>{h.energy}</span>
            </motion.div>
          </div>
          <div className="lg:col-span-6 lg:pl-6"><ProductFrame /></div>
        </div>

        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.9, ease: EASE }}
          className="mt-16 grid gap-6 border border-white/12 bg-white/[0.03] p-6 backdrop-blur-sm md:grid-cols-12 md:items-center md:gap-0 md:p-8" data-testid="hero-price-strip">
          <div className="md:col-span-5" data-testid="hero-price">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--steel-dim)]">{h.from}</p>
            <p className="font-display text-6xl font-black leading-none text-[var(--gold)] sm:text-7xl">
              {eur(9900)} <span className="whitespace-nowrap text-2xl font-bold text-white sm:text-3xl">{h.perUnit}</span>
            </p>
          </div>
          <div className="grid gap-5 sm:grid-cols-2 md:col-span-4 md:grid-cols-2">
            <Fact label={h.depositLabel} value={h.deposit} testid="hero-deposit" />
            <Fact label={h.leadLabel} value={h.lead} testid="hero-lead" />
          </div>
          <div className="md:col-span-3 md:pl-6">
            <Link to="/commander" data-testid="hero-order-btn" className="group flex w-full items-center justify-between gap-3 bg-[var(--gold)] px-6 py-5 text-sm font-bold uppercase tracking-[0.16em] text-[var(--night)] transition-colors hover:bg-[var(--gold-hi)]">
              {t.common.order}
              <ArrowUpRight className="h-5 w-5 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
            <Link to="/#tarifs" data-testid="hero-pricing-link" className="mt-3 block text-center text-xs uppercase tracking-[0.16em] text-[var(--steel)] underline-offset-4 hover:underline">{t.common.seePricing}</Link>
          </div>
        </motion.div>
        <div className="mt-5 grid gap-2 text-xs text-[var(--steel-dim)] md:grid-cols-2" data-testid="hero-price-note">
          <p>{h.note}</p>
          <p className="md:text-right">{h.delivery}</p>
        </div>
      </div>
    </section>
  );
};
